import { useEffect, useRef, useState } from "react";
import { ArrowUp } from "lucide-react";
import { cn } from "@/lib/utils";
import { fmtTime } from "@/lib/trip";

const dayLabel = (iso) => {
  const d = new Date(iso);
  const t = new Date();
  const y = new Date(); y.setDate(t.getDate() - 1);
  if (d.toDateString() === t.toDateString()) return "Today";
  if (d.toDateString() === y.toDateString()) return "Yesterday";
  return d.toLocaleDateString("en-US", { weekday: "long", month: "short", day: "numeric" });
};

const URL_RE = /(https?:\/\/[^\s]+|www\.[^\s]+)/gi;
const linkify = (text, mine) =>
  String(text ?? "").split(URL_RE).map((part, i) => {
    if (i % 2 === 0) return part;
    const href = part.startsWith("http") ? part : `https://${part}`;
    return (
      <a
        key={i}
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        data-testid="chat-message-link"
        className={cn("underline underline-offset-2 break-all", mine ? "text-sky-200 hover:text-white" : "text-navy-700 hover:text-navy-900 font-medium")}
      >
        {part}
      </a>
    );
  });

function Bubble({ m, mine }) {
  return (
    <div className={cn("flex", mine ? "justify-end" : "justify-start")} data-testid="chat-message">
      <div className={cn("max-w-[82%] sm:max-w-[70%]")}>
        <div className={cn("whitespace-pre-wrap break-words rounded-2xl px-4 py-2.5 text-[15px] leading-relaxed sm:text-sm", mine ? "rounded-br-md bg-navy-900 text-white" : "rounded-bl-md border border-slate-200 bg-white text-navy-900")}>
          {linkify(m.text, mine)}
        </div>
        <p className={cn("mt-1 px-1 text-[11px] text-slate-400", mine && "text-right")}>{fmtTime(m.at)}</p>
      </div>
    </div>
  );
}

export function ChatThread({ messages, viewer, onSend, placeholder = "Write a message…", suggestions = [], className }) {
  const [text, setText] = useState("");
  const scroller = useRef(null);

  useEffect(() => {
    const el = scroller.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages.length]);

  const submit = (value) => {
    const v = value.trim();
    if (!v) return;
    onSend(v);
    setText("");
  };

  let lastDay = null;
  return (
    <div className={cn("flex h-full min-h-0 flex-col", className)}>
      <div ref={scroller} className="min-h-0 flex-1 space-y-3 overflow-y-auto overscroll-contain bg-slate-50/60 px-4 py-5 sm:px-6" data-testid="chat-history">
        {messages.length === 0 && <p className="py-10 text-center text-sm text-slate-400">No messages yet. Say hello.</p>}
        {messages.map((m) => {
          const label = dayLabel(m.at);
          const show = label !== lastDay;
          lastDay = label;
          return (
            <div key={m.id} className="space-y-3">
              {show && <p className="pt-2 text-center text-[11px] font-semibold uppercase tracking-widest text-slate-400">{label}</p>}
              <Bubble m={m} mine={m.from === viewer} />
            </div>
          );
        })}
      </div>
      <div className="shrink-0 border-t border-slate-200 bg-white px-3 pb-3 pt-2.5 sm:px-4">
        {suggestions.length > 0 && (
          <div className="no-scrollbar mb-2.5 flex gap-2 overflow-x-auto">
            {suggestions.map((s, i) => (
              <button key={s} data-testid={`chat-suggestion-${i}`} onClick={() => submit(s)} className="shrink-0 rounded-full border border-slate-200 px-3 py-1.5 text-xs font-medium text-navy-700 transition-colors hover:bg-slate-50">
                {s}
              </button>
            ))}
          </div>
        )}
        <form onSubmit={(e) => { e.preventDefault(); submit(text); }} className="flex items-end gap-2">
          <input
            data-testid="chat-input-field"
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder={placeholder}
            enterKeyHint="send"
            autoComplete="off"
            className="h-11 min-w-0 flex-1 rounded-full border border-slate-200 bg-slate-50 px-4 text-base text-navy-900 outline-none transition-colors placeholder:text-slate-400 focus:border-navy-500 focus:bg-white sm:text-sm"
          />
          <button type="submit" data-testid="chat-send-btn" disabled={!text.trim()} aria-label="Send message" className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-navy-900 text-white transition-colors hover:bg-navy-800 disabled:bg-slate-200 disabled:text-slate-400">
            <ArrowUp className="h-5 w-5" />
          </button>
        </form>
      </div>
    </div>
  );
}
